import React, { useCallback, useEffect, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Center, Environment, GizmoHelper, GizmoViewport, Grid, OrbitControls } from "@react-three/drei";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase-config";
import * as THREE from "three";
import { useAuth } from "./contexts/AuthContext";
import { createUserGenerationDoc } from "./handlers/db";
import { GenerationTrigger } from "./types";
import { toast } from "sonner";
import Header from "./components/Header";
import { BoxIcon } from "./components/Icons";
import { Progress } from "./components/ui/progress";
import { NavLink, useLocation } from "react-router-dom";
import SetSceneBackgroundColor from "./components/SetSceneBackgroundColor";
import Result from "./components/Result";
import SidebarArrow from "./components/SidebarArrow";
import { Accordion as UIAccordion, AccordionItem as UIAccordionItem, AccordionTrigger, AccordionContent } from "./components/ui/accordion";
import PaywallModal from "./components/PaywallModal";
import TextToImage from "./components/TextToImage";
import CharacterGeneratorRefactored from "./components/CharacterGeneratorRefactored";
import { FileUploadArea, ModelDownloadSection } from "./components/common/uiHelper";
import { uploadFileToStorage } from './utils/upload';
import { getEndpoints } from './utils/api';
import logger from './utils/vercelRuntimeLogger';
import FloatingToolbar from "./components/FloatingToolbar";
import PolyCount from "./components/PolyCount";
import { downloadFile } from "./utils/misc";
import { Button } from "./components/ui/button";
import DebugPanel from "./components/DebugPanel";
import LoggingDiagnostic from "./components/LoggingDiagnostic";
import ApiHealthCheck from "./components/ApiHealthCheck";
import HeroPreview from "./components/HeroPreview";
import SceneErrorBoundary from "./components/SceneErrorBoundary";
import { GenerationErrorHandler, RetryHandler, NetworkMonitor } from "./utils/errorHandler";
import { BootstrapError, normalizeError } from "./utils/appErrors";
import { GenerationProgress } from "./components/GenerationProgress";
import ModelEntrance from "./components/ModelEntrance";
import ViewerControlsHint from "./components/ViewerControlsHint";
import {
  GENERATION_TIMEOUT_SECONDS,
  IMAGE_TO_3D_STAGES,
  INPUT_QUALITY_TIPS,
  MAX_UPLOAD_BYTES,
} from "./constants";
import {
  clearPendingGeneration,
  loadPendingGeneration,
  savePendingGeneration,
} from "./utils/pendingGeneration";
import {
  NotifyPermissionState,
  clearTabAttention,
  flagTabAttention,
  getNotifyState,
  requestNotifyPermission,
  showCompletionNotification,
} from "./utils/notify";
import { usePrefersReducedMotion } from "./hooks/usePrefersReducedMotion";


  // Add this new component for the spinning cubes
function SpinningCube({
  position,
  loading,
  tilted = false,
  reducedMotion = false,
}: {
  position: [number, number, number];
  loading: boolean;
  tilted?: boolean;
  reducedMotion?: boolean;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const speedRef = useRef(3); // Initial speed

  useFrame((state, delta) => {
    if (reducedMotion) return;
    if (loading && meshRef.current) {
      speedRef.current += 0.2 * delta;
      meshRef.current.rotation.y += speedRef.current * delta;
    } else {
      speedRef.current = 3;
    }
  });

  return (
    <Center top position={position}>
      <mesh
        ref={meshRef}
        castShadow
        rotation={tilted ? [Math.PI / 4, 0, Math.PI / 4] : [0, 0, 0]}
      >
        <boxGeometry args={[0.7, 0.7, 0.7]} />
        <meshStandardMaterial color="#6d3434" />
      </mesh>
    </Center>
  );
}

function shouldEnableRuntimeDebugTools() {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "development";
  }

  const params = new URLSearchParams(window.location.search);
  let localDebugFlag = false;

  try {
    localDebugFlag = localStorage.getItem("debugTools") === "true";
  } catch {
    localDebugFlag = false;
  }

  return (
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_ENABLE_DEBUG_TOOLS === "true" ||
    params.get("debugTools") === "true" ||
    localDebugFlag
  );
}

function SceneCanvasFallback({
  hasModel,
  onRetry,
}: {
  hasModel: boolean;
  onRetry: () => void;
}) {
  return (
    <div
      className="h-full w-full flex items-center justify-center px-6"
      style={{ background: "var(--color-secondary-background)" }}
    >
      <div className="max-w-md rounded-xl border border-border bg-background/95 p-6 text-center shadow-xl">
        <div className="text-lg font-semibold text-foreground">
          {hasModel ? "3D viewer unavailable" : "Preview unavailable"}
        </div>
        <p className="mt-2 text-sm text-foreground/70">
          The rest of the app is still available. Retry the scene or continue using the upload tools.
        </p>
        <Button className="mt-4 w-full" onClick={onRetry}>
          Retry 3D Scene
        </Button>
      </div>
    </div>
  );
}

enum AccordionKeys {
  IMAGE_TO_3D = "ImageTo3D",
  TEXT_TO_IMAGE = "TextToImage",
  CHARACTER_GENERATOR = "CharacterGenerator",
  MODEL_VIEWER = "ModelViewer",
}

function App() {
  // Replace useState with useControls
  const gridConfig = {
    gridSize: [10.5, 10.5],
    cellSize: 0.6,
    cellThickness: 1,
    cellColor: "#9d4b4b",
    sectionSize: 3.3,
    sectionThickness: 1.5,
    sectionColor: "#9d4b4b",
    fadeDistance: 25,
    fadeStrength: 1,
    followCamera: false,
    infiniteGrid: true,
  };
  const { gridSize, ...restGridConfig } = gridConfig;

  const location = useLocation();
  
  // Stores the uploaded image files
  const [images, setImages] = useState<File[]>([]);
  
  // Stores URLs for image preview thumbnails
  const [previews, setPreviews] = useState<string[]>([]);

  // Image-to-3D generation persisted across full page reloads (e.g. /profile
  // navigation) so an in-flight job isn't silently orphaned. Only the
  // image-to-3D flow writes this record — see pendingGeneration.ts.
  const [restoredSession] = useState(loadPendingGeneration);

  // Tracks the ID of currently processing 3D generation job
  const [pendingJob, setPendingJob] = useState<string | null>(
    restoredSession?.generationId ?? null,
  );

  // Indicates if a 3D mesh is currently being generated/loaded
  const [meshLoading, setMeshLoading] = useState<boolean>(Boolean(restoredSession));
  // Per-feature generating states to avoid cross-disabling buttons
  const [isImgTo3DGenerating, setIsImgTo3DGenerating] = useState<boolean>(
    Boolean(restoredSession),
  );
  const [isCharacterGenerating, setIsCharacterGenerating] = useState<boolean>(false);
  const [, setActiveGenerator] = useState<"imageTo3D" | "character" | null>(null);

  // Epoch ms when the current image-to-3D generation started (null = none).
  // The character flow never sets this — it keys the timeout below to this flow.
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(
    restoredSession?.startedAt ?? null,
  );

  // Remote image URLs for a restored session (object URLs don't survive reloads)
  const [restoredImageUrls, setRestoredImageUrls] = useState<string[]>(
    restoredSession?.uploadedUrls ?? [],
  );

  // Inline failure message for the image-to-3D flow (paired with a retry button)
  const [imgTo3DError, setImgTo3DError] = useState<string | null>(null);

  // Web Notification permission state driving the "Notify me" soft-ask
  const [notifyState, setNotifyState] = useState<NotifyPermissionState>(getNotifyState);

  // Shows the one-time viewer-controls hint after a generation completes
  const [showViewerHint, setShowViewerHint] = useState(false);

  // Persistent polite live-region message for screen readers (async completions)
  const [liveMessage, setLiveMessage] = useState("");

  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Stores URL of the generated 3D model result
  const [result, setResult] = useState<string | null>(
    location.state?.result || null,
  );
  
  // Controls visibility of premium features modal
  const [paywallModal, setPaywallModal] = useState<boolean>(false);
  
  // Controls visibility of the left sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Toggles between solid and wireframe view of 3D model
  const [showWireframe, setShowWireframe] = useState(false);
  
  // Indicates if the 3D model has finished loading
  const [modelLoaded, setModelLoaded] = useState(false);
  
  // Tracks which accordion sections are currently expanded. Image to 3D starts
  // open so first-time visitors land on a visible path to the core action.
  const [selectedAccordions, setSelectedAccordions] = useState<AccordionKeys[]>([
    AccordionKeys.IMAGE_TO_3D,
  ]);
  
  // Selected format for model download (glb, obj, etc)
  const [selectedFormat, setSelectedFormat] = useState("glb");
  const [sceneRenderKey, setSceneRenderKey] = useState(0);
  
  // Indicates if a model is currently being downloaded
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Authentication context values for current user
  const { currentUser, signInWithGoogle } = useAuth();
  
  
  // Reference to the 3D model mesh for manipulation
  const meshRef = useRef<THREE.Group>(null);
  
  // Tracks current displayed mesh URL (may differ from result during processing)
  const [currentMeshUrl, setCurrentMeshUrl] = useState(result);

  // In the App component, update the state
  // For model preview visibility in Model Viewer File Upload Area UI
  const [modelViewerPreviewVisible, setModelViewerPreviewVisible] = useState(false);
  
  // Helper function to show toast
  const showToastMessage = useCallback((message: string) => {
    toast(message);
  }, []);

  useEffect(() => {
    return () => {
      previews.forEach((previewUrl) => {
        URL.revokeObjectURL(previewUrl);
      });
    };
  }, [previews]);

  useEffect(() => {
    if (!shouldEnableRuntimeDebugTools()) {
      return;
    }

    let cancelled = false;

    Promise.all([
      import("./utils/webhookTestHelper"),
      import("./utils/debugCharacterGeneration"),
    ])
      .then(() => {
        if (cancelled) {
          return;
        }

        logger.info("DebugTools", "RUNTIME_DEBUG_TOOLS_ENABLED", {
          correlationId: logger.createCorrelationId("debug"),
          environment: process.env.NODE_ENV,
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        logger.error("DebugTools", "RUNTIME_DEBUG_TOOLS_FAILED", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Handles the selection of multiple image files
  const handleMultipleFilesSelect = useCallback(
    (files: File[]) => {
      const typedFiles = files.filter(
        (file) => file.type === "image/png" || file.type === "image/jpeg",
      );
      const rejectedTypeCount = files.length - typedFiles.length;
      if (rejectedTypeCount > 0) {
        showToastMessage(
          rejectedTypeCount === files.length
            ? "Only PNG and JPG images are supported"
            : `${rejectedTypeCount} file${rejectedTypeCount > 1 ? "s were" : " was"} skipped — only PNG and JPG images are supported`,
        );
      }

      const validFiles = typedFiles.filter((file) => {
        if (file.size <= MAX_UPLOAD_BYTES) return true;
        showToastMessage(`"${file.name}" is over 20MB — please use a smaller image`);
        return false;
      });

      if (validFiles.length === 0) return;

      setImages(validFiles);

      // Generate previews
      const previewUrls = validFiles.map((file) => URL.createObjectURL(file));
      setPreviews(previewUrls);
    },
    [showToastMessage],
  );

  // One-click sample so first-time visitors can see the full flow without
  // hunting for a suitable photo of their own.
  const handleUseExampleImage = useCallback(async () => {
    try {
      const response = await fetch("/turnaround-ex1-1.png");
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const file = new File([blob], "example-image.png", { type: "image/png" });
      handleMultipleFilesSelect([file]);
    } catch (error) {
      logger.warn("ImageTo3D", "EXAMPLE_IMAGE_FAILED", { error: String(error) });
      showToastMessage("Couldn't load the example image — please upload your own.");
    }
  }, [handleMultipleFilesSelect, showToastMessage]);

  const handleNotifyClick = useCallback(async () => {
    setNotifyState(await requestNotifyPermission());
  }, []);

  // Handles drag and drop of image files
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      handleMultipleFilesSelect(files);
    },
    [handleMultipleFilesSelect],
  );

  // Handles file input change event
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleMultipleFilesSelect(Array.from(e.target.files));
      }
    },
    [handleMultipleFilesSelect],
  );

  // Determine active input can be derived inline where needed.

  const triggerGeneration = useCallback(async (imageFile?: File) => {
    logger.info('ImageTo3D', 'GENERATION_START', {
      imageFileName: imageFile?.name,
      imageFileSize: imageFile?.size,
      imagesCount: images.length,
      currentUser: currentUser?.uid,
      plan: currentUser?.plan,
      generationsLeft: currentUser?.generationsLeft
    });

    // If we have a imageFile, use it. Otherwise check images state
    if (!imageFile && images.length === 0) {
      logger.warn('ImageTo3D', 'NO_IMAGES', { 
        imageFile: !!imageFile, 
        imagesLength: images.length 
      });
      showToastMessage("Please provide images first");
      return;
    }
    
    const startedAtMs = Date.now();
    setModelLoaded(false);
    setMeshLoading(true);
    setIsImgTo3DGenerating(true);
    setActiveGenerator("imageTo3D");
    setImgTo3DError(null);
    setGenerationStartedAt(startedAtMs);
    clearTabAttention();

    logger.info('ImageTo3D', 'UI_STATE_UPDATE', {
      meshLoading: true,
      isGenerating: true,
      activeGenerator: 'imageTo3D'
    });

    let curUser = currentUser;

    try {
      // If not logged in, wait for sign in to complete
      if (!currentUser) {
        logger.info('ImageTo3D', 'AUTH_REQUIRED', { hasUser: false });
        curUser = await signInWithGoogle();
        // Add a small delay to ensure auth state is updated
        await new Promise((resolve) => setTimeout(resolve, 1000));
        logger.info('ImageTo3D', 'AUTH_COMPLETED', { userId: curUser?.uid });
      }

      // Verify user is now logged in
      if (!curUser) {
        logger.error('ImageTo3D', 'AUTH_FAILED', new Error('Authentication failed'));
        throw new Error("Authentication failed");
      }

      if (
        (curUser.plan === "FREE" || !curUser.plan) &&
        curUser.generationsLeft <= 0
      ) {
        logger.warn('ImageTo3D', 'QUOTA_EXCEEDED', {
          plan: curUser.plan,
          generationsLeft: curUser.generationsLeft
        });
        setMeshLoading(false);
        setIsImgTo3DGenerating(false);
        setGenerationStartedAt(null);
        setPaywallModal(true);
        return;
      }

      // Use the imageFile if provided, otherwise use images state
      const filesToUpload = imageFile ? [imageFile] : images;
      
      logger.info('ImageTo3D', 'UPLOAD_START', {
        fileCount: filesToUpload.length,
        totalSize: filesToUpload.reduce((sum, f) => sum + f.size, 0),
        fileNames: filesToUpload.map(f => f.name)
      });
      
      const uploadPromises = filesToUpload.map((image, index) => 
        uploadFileToStorage(image, `image${index + 1}.png`)
      );
      
      const uploadedUrls = await Promise.all(uploadPromises);
      
      logger.info('ImageTo3D', 'UPLOAD_COMPLETE', {
        uploadedCount: uploadedUrls.length,
        urls: uploadedUrls
      });

      if (!curUser) {
        throw new Error("User must be logged in");
      }

      logger.info('ImageTo3D', 'CREATE_FIREBASE_DOC_START', {
        userId: curUser.uid
      });
      
      const docId = await createUserGenerationDoc(curUser);
      // Extract just the document ID from the full path
      const generationId = docId.split("/").pop()!;
      
      logger.info('ImageTo3D', 'CREATE_FIREBASE_DOC_SUCCESS', {
        docId,
        generationId
      });

      const generationTrigger: GenerationTrigger = {
        docId,
        image: null,
        images: uploadedUrls,
      };

      const { generateUrl } = getEndpoints();
      // Optional queue name via env var if backend expects it (e.g., Runpod route or queue)
      const queue = process.env.REACT_APP_GENERATE_QUEUE;
      const url = queue ? `${generateUrl}?queueName=${encodeURIComponent(queue)}` : generateUrl;
      
      logger.info('ImageTo3D', 'API_CALL_START', {
        url,
        docId,
        imagesCount: uploadedUrls.length,
        queue
      });
      
      // Check network connectivity first
      if (!NetworkMonitor.isOnline()) {
        throw new Error('No internet connection. Please check your network and try again.');
      }
      
      const response = await RetryHandler.withRetry(
        async () => {
          const res = await logger.trackApiCall(
            'ImageTo3D',
            url,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(generationTrigger)
            },
            async () => {
              const fetchResponse = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(generationTrigger)
              });
              if (!fetchResponse.ok) {
                const errorText = await fetchResponse.text();
                logger.error('ImageTo3D', 'API_ERROR_RESPONSE', new Error(errorText), {
                  status: fetchResponse.status,
                  statusText: fetchResponse.statusText,
                  responseBody: errorText
                });
                throw new Error(`API Error (${fetchResponse.status}): ${errorText || fetchResponse.statusText}`);
              }
              return fetchResponse;
            }
          );
          return res;
        },
        {
          maxRetries: 3,
          onRetry: (attempt, error) => {
            showToastMessage(`Retrying request (attempt ${attempt}/3)...`);
            logger.warn('ImageTo3D', 'API_RETRY', { attempt, error: error.message });
          }
        }
      );
      
      const responseData = await response.json();
      logger.info('ImageTo3D', 'API_CALL_SUCCESS', {
        responseData,
        requestId: responseData?.requestId
      });
      
      setPendingJob(generationId); // Set pending job in App component
      savePendingGeneration({
        flow: "imageTo3D",
        generationId,
        startedAt: startedAtMs,
        uploadedUrls,
      });
      logger.info('ImageTo3D', 'GENERATION_TRACKING_STARTED', {
        generationId,
        docId
      });
    } catch (error) {
      const errorInfo = GenerationErrorHandler.analyzeError(error);
      
      logger.error('ImageTo3D', 'GENERATION_FAILED', error, {
        stage: 'triggerGeneration',
        userId: curUser?.uid,
        errorType: errorInfo.type,
        errorDetails: errorInfo.details
      });
      
      setMeshLoading(false); // Ensure loading is turned off in App component
      setIsImgTo3DGenerating(false);
      setGenerationStartedAt(null);
      clearPendingGeneration();

      // Show user-friendly error message with suggested action
      let displayMessage = errorInfo.message;
      if (errorInfo.suggestedAction) {
        displayMessage += ` ${errorInfo.suggestedAction}.`;
      }

      // Inline error panel (with retry) — uploaded images stay loaded
      setImgTo3DError(displayMessage);
      setLiveMessage("Generation failed");
      showToastMessage(displayMessage);
      
      // Log additional debug info in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Generation Error Details:', errorInfo);
      }
      
      return;
    }
  }, [images, currentUser, signInWithGoogle, showToastMessage]);

  const handleDownload = async () => {
    try {
      const urlToDownload = currentMeshUrl || result;
      if (!urlToDownload) {
        throw new Error("No model available to download");
      }
      setIsDownloading(true);
      const minDurationPromise = new Promise(resolve => setTimeout(resolve, 500));
      
      // Show conversion toast for non-GLB formats
      if (selectedFormat !== 'glb') {
        showToastMessage("Converting Model Format...");
      }

      const downloadPromise = (async () => {
        if (selectedFormat === "glb") {
          await downloadFile(urlToDownload, "model.glb");
          return;
        } else {
          const { convertUrl } = getEndpoints();
          const response = await fetch(convertUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url: urlToDownload, downloadType: selectedFormat }),
          });
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Conversion failed. Please try again. Response: ${errorText}`);
          }
          const blob = await response.blob();
          const fileName = `model.${selectedFormat}`;

          await downloadFile(URL.createObjectURL(blob), fileName);
        }
      })();
      await Promise.all([minDurationPromise, downloadPromise]);
    } catch (error) {
      console.error("Error downloading model: ", error);
      alert("Failed to convert model. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSelectTextGeneratedImage = useCallback(async(url: string) => {
    try {
        let blob: Blob;
        
        // Check if it's a data URL or regular URL
        if (url.startsWith('data:')) {
            // Handle data URL (base64)
            const base64Response = await fetch(url);
            blob = await base64Response.blob();
        } else {
            // Handle regular URL
            const response = await fetch(url);
            blob = await response.blob();
        }
        
        const file = new File([blob], "generated-image.png", { type: "image/png" });
        
        setImages([file]);
        setPreviews([url]);
        
        setSelectedAccordions([AccordionKeys.IMAGE_TO_3D]);
        
        await triggerGeneration(file);
        
    } catch (error) {
        console.error("Error processing image:", error);
        showToastMessage("Failed to process image. Please try again.");
    }
}, [triggerGeneration, setSelectedAccordions, showToastMessage]);

  // const activeInput: ACTIVE = determineActiveInput();

  useEffect(() => {
    if (!pendingJob || !currentUser) return;

    logger.info('ImageTo3D', 'FIREBASE_LISTENER_START', {
      pendingJob,
      userId: currentUser.uid,
      docPath: `User/${currentUser.uid}/Generation/${pendingJob}`
    });

    const unsubscribe = onSnapshot(
      doc(db, "User", currentUser.uid, "Generation", pendingJob),
      (doc) => {
        const data = doc.data();
        
        logger.info('ImageTo3D', 'FIREBASE_STATUS_UPDATE', {
          status: data?.status,
          dirPath: data?.dirPath,
          errorMessage: data?.errorMessage,
          fullData: data,
          pendingJob
        });
        
        if (data?.status === "DONE") {
          const modelUrl = `https://photoshopai.blob.core.windows.net/3dstudio/${data.dirPath}/result.glb`;
          
          logger.info('ImageTo3D', 'GENERATION_SUCCESS', {
            modelUrl,
            dirPath: data.dirPath,
            pendingJob,
            processingTime: data?.processingTime
          });
          
          setResult(modelUrl);
          setMeshLoading(false);
          setPendingJob(null);
          // Clear per-feature generating flags
          setIsImgTo3DGenerating(false);
          setIsCharacterGenerating(false);
          setActiveGenerator(null);
          setGenerationStartedAt(null);
          setImgTo3DError(null);
          setRestoredImageUrls([]);
          clearPendingGeneration();

          toast.success("Your 3D model is ready");
          setLiveMessage("Your 3D model is ready");
          setShowViewerHint(true);
          // Pull users back if they tabbed away during the wait
          flagTabAttention("success");
          showCompletionNotification(true);

        } else if (data?.status === "ERROR") {
          logger.error('ImageTo3D', 'GENERATION_ERROR', 
            new Error(data?.errorMessage || 'Generation failed'), 
            {
              pendingJob,
              dirPath: data?.dirPath,
              errorDetails: data?.error,
              fullData: data
            }
          );
          
          setMeshLoading(false); // Ensure loading is turned off in App component
          setPendingJob(null); // Clear pending job in App component
          setIsImgTo3DGenerating(false);
          setIsCharacterGenerating(false);
          setActiveGenerator(null);
          setGenerationStartedAt(null);
          clearPendingGeneration();

          const errorMsg = data?.errorMessage || "Generation failed. Please try again.";
          setImgTo3DError(errorMsg);
          setLiveMessage("Generation failed");
          flagTabAttention("error");
          showCompletionNotification(false);
          showToastMessage(errorMsg);
        } else if (data?.status === "PENDING") {
          logger.debug('ImageTo3D', 'GENERATION_PENDING', {
            pendingJob,
            elapsed: Date.now() - (data?.created?.toMillis() || Date.now())
          });
        }
      },
      (error) => {
        logger.error('ImageTo3D', 'FIREBASE_LISTENER_ERROR', error, {
          pendingJob,
          userId: currentUser.uid
        });
      }
    );

    return () => unsubscribe();
  }, [pendingJob, currentUser, showToastMessage]);

  // Honest client-side timeout for the image-to-3D wait: without it, a stuck
  // backend means an infinite spinner. Keyed off generationStartedAt, which the
  // character flow never sets (pendingJob alone is shared between flows).
  useEffect(() => {
    if (!pendingJob || generationStartedAt === null) return;
    const remainingMs =
      GENERATION_TIMEOUT_SECONDS * 1000 - (Date.now() - generationStartedAt);
    const timeout = setTimeout(() => {
      logger.warn("ImageTo3D", "CLIENT_TIMEOUT", {
        pendingJob,
        elapsedMs: Date.now() - generationStartedAt,
      });
      setPendingJob(null);
      setGenerationStartedAt(null);
      setMeshLoading(false);
      setIsImgTo3DGenerating(false);
      clearPendingGeneration();
      setImgTo3DError(
        "This is taking much longer than expected. The job may still finish — check your generation library from your profile in a few minutes, or try again.",
      );
      setLiveMessage("Generation is taking longer than expected");
    }, Math.max(0, remainingMs));
    return () => clearTimeout(timeout);
  }, [pendingJob, generationStartedAt]);

  // Set currentMeshUrl to result when result changes
  useEffect(() => {
    setCurrentMeshUrl(result);
  }, [result]);

  // Model upload handler
  const handleModelUpload = async (file: File) => {
    try {
      setMeshLoading(true);
      
      if (file.name.endsWith('.glb')) {
        const returnedModelUrl = await uploadFileToStorage(file, "uploaded-model.glb");
        setResult(returnedModelUrl);
        setCurrentMeshUrl(returnedModelUrl);
      }
      else {
        let returnedModelUrl = "";
        if (file.name.endsWith('.obj')) {
          returnedModelUrl = await uploadFileToStorage(file, "uploaded-model.obj");
        } 
        if (file.name.endsWith('.fbx')) {
          returnedModelUrl = await uploadFileToStorage(file, "uploaded-model.fbx");
        }

        // Convert to GLB
        const { convertUrl } = getEndpoints();
        const response = await fetch(convertUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: returnedModelUrl, downloadType: "glb" }),
        });
        if (!response.ok) {
          const errorText = await response.text();
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.detail || 'Conversion failed. Please try again.');
        }

        // Get GLB blob and upload it to storage
        const blob = await response.blob();
        const convertedFile = new File([blob], "converted-model.glb", { type: "model/gltf-binary" });
        const glbUrl = await uploadFileToStorage(convertedFile, "converted-model.glb");
        
        setResult(glbUrl);
        setCurrentMeshUrl(glbUrl);
      }

      setModelLoaded(false);
      setModelViewerPreviewVisible(true);
      showToastMessage("Model uploaded successfully!");
    } catch (error) {
      console.error('Error uploading model:', error);
      showToastMessage("Error uploading model. Please try again.");
    } finally {
      setMeshLoading(false);
    }
  };

  // Model drop handler
  const handleModelDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.glb') || file.name.endsWith('.obj') || file.name.endsWith('.fbx'))) {
      await handleModelUpload(file);
    } else {
      showToastMessage("Please upload a .glb file");
    }
  };

  // Model file input handler
  const handleModelFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.name.endsWith('.glb') || file.name.endsWith('.obj') || file.name.endsWith('.fbx'))) {
      await handleModelUpload(file);
    }
  };

  return (
    <div className="relative w-full h-screen max-h-screen flex flex-col bg-background">
      {/* Persistent polite live region: async completions are announced to
          screen readers without moving focus. Must stay mounted permanently. */}
      <div role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </div>
      <PaywallModal
        isOpen={paywallModal}
        onClose={() => setPaywallModal(false)}
        user={currentUser}
      />
      
      {/* Debug Panel - only shows in development */}
      <DebugPanel show={process.env.NODE_ENV === 'development'} />
      
      {/* API Health Check - shows in development or when there are errors */}
      {process.env.NODE_ENV === 'development' && <ApiHealthCheck />}
      
      {/* Logging Diagnostic - shows when ?diagnostic=true is in URL */}
      {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('diagnostic') === 'true' && (
        <LoggingDiagnostic />
      )}

      <Header />
      <SidebarArrow sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative h-full">
        {/*Left Navbar*/}
        <div
          className={`bg-background sidebar-backdrop border-2 border-border h-full flex flex-col transition-all overflow-hidden neo-shadow absolute left-0 z-10`}
          style={{
            maxWidth: sidebarOpen ? "min(500px, 100vw)" : "20px",
            minWidth: sidebarOpen ? "min(500px, 100vw)" : "20px",
          }}
        >
          {/* Scrollable content area */}
          <div 
            className="flex-1 overflow-y-auto"
            style={{ opacity: sidebarOpen ? 1 : 0 }}
          >
            <div className="px-12 pt-8">
              <UIAccordion
                type="multiple"
                value={selectedAccordions as string[]}
                onValueChange={(v) => setSelectedAccordions(v as AccordionKeys[])}
                data-testid="accordion"
                className="space-y-3"
              >
                <UIAccordionItem value={AccordionKeys.IMAGE_TO_3D}>
                  <AccordionTrigger>Image to 3D</AccordionTrigger>
                  <AccordionContent>
                    <div className="text-center pt-2 pb-10">
                      <div className="text-foreground text-2xl font-semibold mb-4 select-none">
                        Try it for Free
                      </div>
                      <div className="text-foreground/80 text-lg font-medium">
                        Upload an image or drop a file to generate a stunning 3D
                        model instantly
                      </div>
                    </div>

                    <FileUploadArea
                      onDrop={handleDrop}
                      onBrowse={handleFileInput}
                      fileInputId="fileInput"
                      accept="image/png,image/jpeg"
                      uploadText="Drop your image here"
                      previewUrls={previews}
                      previewType="image"
                      clearPreview={() => {
                        setImages([]);
                        previews.forEach(URL.revokeObjectURL);
                        setPreviews([]);
                      }}
                    />
                    <p className="text-sm text-foreground/60 text-center -mt-2 mb-2">
                      PNG or JPG, up to 20MB. A single subject on a plain
                      background works best.
                    </p>
                    {images.length === 0 && !isImgTo3DGenerating && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full mb-2"
                        onClick={handleUseExampleImage}
                      >
                        Try an example image
                      </Button>
                    )}
                    <Button className="w-full mt-4" onClick={() => triggerGeneration()} disabled={images.length === 0 || isImgTo3DGenerating}>
                      {isImgTo3DGenerating ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-main border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
                          Generating...
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 justify-center">
                          <BoxIcon /> Generate 3D Model
                        </span>
                      )}
                    </Button>
                    {isImgTo3DGenerating && generationStartedAt !== null && (
                      <GenerationProgress
                        startedAt={generationStartedAt}
                        stages={IMAGE_TO_3D_STAGES}
                        imageUrls={previews.length > 0 ? previews : restoredImageUrls}
                        tips={INPUT_QUALITY_TIPS}
                        pinnedStageIndex={pendingJob ? null : 0}
                        notifyState={notifyState}
                        onNotifyClick={handleNotifyClick}
                      />
                    )}
                    {imgTo3DError && !isImgTo3DGenerating && (
                      <div
                        role="alert"
                        className="border-2 border-danger bg-danger/10 rounded-lg neo-shadow p-4 mt-4"
                      >
                        <p className="text-sm font-semibold text-foreground">
                          {imgTo3DError}
                        </p>
                        {images.length > 0 && (
                          <Button
                            type="button"
                            className="w-full mt-3"
                            onClick={() => triggerGeneration()}
                          >
                            Try again
                          </Button>
                        )}
                      </div>
                    )}
                    {result && (
                      <ModelDownloadSection
                        result={result}
                        selectedFormat={selectedFormat}
                        setSelectedFormat={setSelectedFormat}
                        handleDownload={handleDownload}
                        isDownloading={isDownloading}
                      />
                    )}
                  </AccordionContent>
                </UIAccordionItem>
                <UIAccordionItem value={AccordionKeys.MODEL_VIEWER}>
                  <AccordionTrigger>3D Model Viewer</AccordionTrigger>
                  <AccordionContent>
                    <div className="text-center pt-2 pb-6 select-none">
                      {meshLoading ? (
                        <div className="flex items-center justify-center gap-2 text-foreground/80">
                          <div className="w-5 h-5 border-2 border-main border-t-transparent rounded-full animate-spin" />
                          <span>Loading model...</span>
                        </div>
                      ) : (
                        <div className="text-foreground/80 text-lg font-medium">
                          Upload a glb/obj/fbx file to view the 3D model
                        </div>
                      )}
                    </div>
                    <FileUploadArea
                      onDrop={handleModelDrop}
                      onBrowse={handleModelFileChange}
                      fileInputId="modelFileInput"
                      accept=".glb,.obj,.fbx"
                      uploadText="Drop your model here"
                      previewType="model"
                      previewName={modelViewerPreviewVisible ? 'true' : null}
                      clearPreview={() => {
                        setModelViewerPreviewVisible(false);
                      }}
                    />
                    {result && (
                      <ModelDownloadSection
                        result={result}
                        selectedFormat={selectedFormat}
                        setSelectedFormat={setSelectedFormat}
                        handleDownload={handleDownload}
                        isDownloading={isDownloading}
                      />
                    )}
                  </AccordionContent>
                </UIAccordionItem>
                <UIAccordionItem value={AccordionKeys.TEXT_TO_IMAGE}>
                  <AccordionTrigger>Text to Image</AccordionTrigger>
                  <AccordionContent>
                    <div className="text-center pt-2 pb-6 select-none">
                      <div className="text-foreground/80 text-lg font-medium">
                        Generate an image from text, then make it 3D
                      </div>
                    </div>
                    <TextToImage onSelectImage={handleSelectTextGeneratedImage} />
                  </AccordionContent>
                </UIAccordionItem>
                <UIAccordionItem value={AccordionKeys.CHARACTER_GENERATOR}>
                  <AccordionTrigger>Character Generator</AccordionTrigger>
                  <AccordionContent>
                    <CharacterGeneratorRefactored 
                      setPendingJob={setPendingJob}
                      setMeshLoading={setMeshLoading}
                      characterGenerating={isCharacterGenerating}
                      setCharacterGenerating={setIsCharacterGenerating}
                      onStartGeneration={() => {
                        setIsCharacterGenerating(true);
                        setActiveGenerator("character");
                      }}
                      onGenerationComplete={(resultUrl) => {
                        setResult(resultUrl);
                        setActiveGenerator(null);
                      }}
                    />
                    {result && (
                      <ModelDownloadSection
                        result={result}
                        selectedFormat={selectedFormat}
                        setSelectedFormat={setSelectedFormat}
                        handleDownload={handleDownload}
                        isDownloading={isDownloading}
                      />
                    )}
                  </AccordionContent>
                </UIAccordionItem>
              </UIAccordion>
            </div>
          </div>

          {/* Fixed bottom section */}
          <div 
            className="border-t-2 border-border p-6 bg-secondary-background neo-shadow z-30"
            style={{ opacity: sidebarOpen ? 1 : 0 }}
          >
            {currentUser?.plan === "FREE" && (
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <p className="text-foreground/80 text-sm">Free Generations Left</p>
                  <p className="text-foreground/80 text-sm">{currentUser.generationsLeft}/3</p>
                </div>

                <Progress value={(currentUser.generationsLeft / 3) * 100} className="max-w-md" />
              </div>
            )}

            {currentUser ? (
              <Button asChild size="sm" className="w-full">
                <NavLink to="/profile">Profile</NavLink>
              </Button>
            ) : (
              <Button size="sm" className="w-full" onClick={() => signInWithGoogle()}>
                Sign In
              </Button>
            )}
          </div>
        </div>

        {result && (
          <>
            {showViewerHint && modelLoaded && <ViewerControlsHint />}
            <FloatingToolbar
              showWireframe={showWireframe}
              setShowWireframe={setShowWireframe}
            />
            {!meshLoading && (
              <PolyCount 
                modelRef={meshRef} 
                modelLoaded={modelLoaded} 
                meshURL={result}
                onMeshUpdate={(newUrl) => {
                  setCurrentMeshUrl(newUrl); 
                  setModelLoaded(false);
                }}
                showToastMessage={showToastMessage}
              />
            )}
          </>
        )}

        {/* Canvas pixels are invisible to assistive tech; the label plus the
            live region above carry the viewer's state instead. */}
        <div
          className="h-full w-full"
          role="img"
          aria-label={
            result
              ? "Interactive 3D model viewer showing your generated model. Drag to orbit, scroll to zoom, right-click drag to pan."
              : "Empty 3D viewport. Your generated model will appear here."
          }
        >
        <SceneErrorBoundary
          key={`${sceneRenderKey}-${currentMeshUrl || result || "empty"}`}
          fallback={
            <SceneCanvasFallback
              hasModel={Boolean(currentMeshUrl || result)}
              onRetry={() => setSceneRenderKey((key) => key + 1)}
            />
          }
          onError={(error, errorInfo) => {
            const correlationId = logger.createCorrelationId("scene");
            logger.error(
              "App",
              "SCENE_RENDER_FAILED",
              new BootstrapError("The 3D scene failed to render.", {
                stage: currentMeshUrl || result ? "model-viewer" : "hero-preview",
                correlationId,
                retryable: true,
                cause: error,
                metadata: {
                  componentStack: errorInfo.componentStack,
                  meshLoading,
                  hasModel: Boolean(currentMeshUrl || result),
                },
              }),
              {
                correlationId,
                componentStack: errorInfo.componentStack,
                meshLoading,
                hasModel: Boolean(currentMeshUrl || result),
                currentMeshUrl,
                normalizedError: normalizeError(error),
              },
            );
          }}
        >
          <Canvas
            shadows
            camera={{ position: [10, 12, 12], fov: 25 }}
            gl={{ alpha: false }}
            style={{ background: "var(--color-secondary-background)" }}
          >
            <SetSceneBackgroundColor />

            <ambientLight intensity={1.5} />
            <directionalLight
              position={[5, 5, 5]}
              intensity={2.5}
              castShadow
            />

            <Environment
              files="potsdamer_platz_1k.hdr"
              background={false}
            />

            <group position={[0, -0.5, 0]}>
              {result ? (
                <ModelEntrance active={modelLoaded}>
                  <Center top>
                    {currentMeshUrl && (
                    <Result
                      url={currentMeshUrl || result}
                      showWireframe={showWireframe}
                      ref={meshRef}
                      materialProps={{
                        roughness: 0.5,
                        metalness: 0.1,
                        envMapIntensity: 1.2,
                      }}
                      onLoad={() => {
                        setModelLoaded(true);
                      }}
                    />
                    )}
                  </Center>
                </ModelEntrance>
              ) : (
                <>
                  <HeroPreview loading={meshLoading} />
                  <SpinningCube
                    position={[-2, 0, 2]}
                    loading={meshLoading}
                    tilted={true}
                    reducedMotion={prefersReducedMotion}
                  />
                  <SpinningCube
                    position={[4, 0, 1]}
                    loading={meshLoading}
                    tilted={false}
                    reducedMotion={prefersReducedMotion}
                  />
                </>
              )}
              <Grid
                position={[0, -0.01, 0]}
                args={[gridSize[0], gridSize[1]]}
                {...restGridConfig}
                receiveShadow={false}
              />
            </group>
            <OrbitControls makeDefault />
            <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
              <GizmoViewport
                axisColors={["#9d4b4b", "#9d4b4b", "#9d4b4b"]}
                labelColor="white"
              />
            </GizmoHelper>
          </Canvas>
        </SceneErrorBoundary>
        </div>
      </div>


    </div>
  );
}

export default App;
